import { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { selectSystemMode } from '../../store/system/system.selector.js';

// styles
import {
    LandingPageContainer,
    LandingHeaderContainer,
} from './LandingPage.styles.js';

// data + hook
import PANELS from './components/app-panels/panel.ts';
import useBounce from '../../hooks/useBounce.ts';

// components
import LandingHeader from './components/landing-header/LandingHeader.js';
import ModeBG from './components/mode-bg/ModeBG.js';
import WhiteElephantPanel from './components/app-panels/WhiteElephantPanel.js';
import TrillionsPanel from './components/app-panels/TrillionsPanel.js';
import PullVaultPanel from './components/app-panels/PullVaultPanel.js';

const PANEL_FULL = 300;
const PANEL_SMALL = Math.round(PANEL_FULL * (2 / 3)); // 200px
const MOBILE_BREAKPOINT = 568;

const PANEL_COMPONENTS = {
    'white-elephant': WhiteElephantPanel,
    'trillions': TrillionsPanel,
    'pull-vault': PullVaultPanel,
};

function getPanelSize() {
    const size = window.innerWidth < MOBILE_BREAKPOINT ? PANEL_SMALL : PANEL_FULL;
    return { w: size, h: size };
}

export default function LandingPage() {
    const systemMode = useSelector(selectSystemMode);
    const containerRef = useRef(null);
    const [panelSize, setPanelSize] = useState(getPanelSize);
    const { positions, setHovered } = useBounce(PANELS, containerRef, panelSize);

    useEffect(() => {
        function handleResize() {
            setPanelSize(getPanelSize());
        }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <LandingPageContainer $mode={systemMode} ref={containerRef}>
            <LandingHeaderContainer>
                <LandingHeader navLabel={'about'} navLink={'/about'} />
            </LandingHeaderContainer>

            {PANELS.map((panel) => {
                const pos = positions[panel.id];
                const PanelComponent = PANEL_COMPONENTS[panel.id];
                if (!pos || !PanelComponent) return null;

                const scale = panelSize.w / PANEL_FULL;

                return (
                    <div
                        key={panel.id}
                        style={{
                            position: 'absolute',
                            left: pos.x,
                            top: pos.y,
                            width: PANEL_FULL,
                            height: PANEL_FULL,
                            transform: `scale(${scale})`,
                            transformOrigin: 'top left',
                            zIndex: 200,
                        }}
                    >
                        <motion.div
                            style={{ width: '100%', height: '100%' }}
                            whileHover={{ scale: 1.06 }}
                            whileTap={{ scale: 0.96 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                            onHoverStart={() => setHovered(panel.id)}
                            onHoverEnd={() => setHovered(null)}
                        >
                            <PanelComponent />
                        </motion.div>
                    </div>
                );
            })}

            <ModeBG />
        </LandingPageContainer>
    );
}
